/*
 * Solitaire table — cards on the felt: where each one lies, how it gets there,
 * and what a finger or a mouse means by touching it. No rules here: the table
 * asks the page (cfg.*) what is allowed and reports what was done.
 *
 * Geometry belongs to CSS. The size of a card is one formula in the blade
 * (container units of the table), a card's place is a handful of custom
 * properties (--x column, --yr row, --yd/--yu cards under it), and the table
 * script only decides those numbers. So the table the server renders and the
 * table the script takes over are the same table, to the pixel.
 *
 * Motion only shows what already happened (docs/why/solitaire.md, «Движение»):
 *   • the state changes at once and every card is set where it will REST —
 *     a tap is tested against those places, never against a card in the air;
 *   • a step is then played back beat by beat (core.js `beats`): the move, the
 *     card it uncovers turning over, each card sent home, a finished Spider suit
 *     leaving — as Web Animations of `transform` only (the compositor runs them);
 *   • any new input finishes every flight at once (`settle`): nothing waits
 *     for an animation, and nothing is ever half-drawn;
 *   • a browser without Element.animate gets the old CSS transitions.
 *
 * One gesture has one owner (docs/why/one-gesture-one-owner.md):
 *   a tap is the browser's `click` — finger tremble never becomes a drag;
 *   a drag starts past a threshold and owns the press from there;
 *   the click the browser sends after a drag is dropped by the press it belongs to.
 */
(function (root) {
    'use strict';

    var S = root.Solitaire;
    var SUITS = ['s', 'h', 'd', 'c'];
    var NS = 'http://www.w3.org/2000/svg';
    // Web Animations came to WebKit with iOS 13.4: older engines keep the CSS transitions.
    var CAN_ANIMATE = typeof Element !== 'undefined' && typeof Element.prototype.animate === 'function';
    // At most this many cards in the air at once; a longer list is spread out in time.
    var CAP = 16, CAP_LIGHT = 7;

    function px(n) { return (Math.round(n * 100) / 100) + 'px'; }
    function now() { return (root.performance && performance.now) ? performance.now() : Date.now(); }
    function at(x, y) { return 'translate(' + px(x) + ',' + px(y) + ')'; }
    // The one door to Element.animate: asked before every call.
    function anim(node, frames, opts) {
        if (!node || typeof node.animate !== 'function') return null;
        try { return node.animate(frames, opts); } catch (_) { return null; }
    }

    function create(el, cfg) {
        var def = null, st = null, game = '';
        var cards = new Map();          // uid → element
        var slots = [];                 // pile index → slot element
        var M = { cw: 0, ch: 0, W: 0, H: 0, gap: 0, rowgap: 0 };
        var opt = { lefty: false, xl: false, side: false, ranks: null };
        var lay = null;
        var drag = null, dragClick = false, raf = 0;
        var hintEls = [], ghosts = [], cursorEl = null, pickedEls = [];
        var flyTimer = new Map();
        var shown = null;               // uid → resting place as last drawn
        var seq = null, seqNo = 0, idleQ = [];
        var light = false, slowRuns = 0;
        var motion = CAN_ANIMATE ? 'waapi' : 'css';
        if (motion === 'css') el.classList.add('sol-css-motion');

        var probe = el.querySelector('.sol-probe');
        if (!probe) {
            probe = document.createElement('i');
            probe.className = 'sol-probe';
            probe.setAttribute('aria-hidden', 'true');
            el.appendChild(probe);
        }
        var probeGap = document.createElement('i');
        probeGap.className = 'sol-probe-gap';
        probeGap.setAttribute('aria-hidden', 'true');
        el.appendChild(probeGap);

        // ── geometry ────────────────────────────────────────────────────────
        function measure() {
            var r = el.getBoundingClientRect();
            M.W = el.clientWidth; M.H = el.clientHeight;
            // Wide and low (a phone on its side, a short laptop window): stock and
            // foundations go to the sides and the columns get the whole height.
            var wantSide = M.H > 0 && M.W / M.H >= 1.62;
            if (wantSide !== opt.side) { opt.side = wantSide; el.classList.toggle('sol-side', wantSide); }
            var pr = probe.getBoundingClientRect(), gr = probeGap.getBoundingClientRect();
            M.cw = pr.width; M.ch = pr.height; M.gap = gr.width; M.rowgap = gr.height;
            M.left = r.left; M.top = r.top;
            M.ox = (M.W - slotCount() * M.cw - (slotCount() - 1) * M.gap) / 2;
            // Browsers without container units: the same formula, computed here.
            if (!(M.cw > 4)) {
                var n = slotCount();
                M.gap = Math.max(3, Math.min(10, M.W * 0.011));
                M.rowgap = Math.max(6, Math.min(16, M.H * 0.022));
                M.cw = Math.min((M.W - (n - 1) * M.gap) / n, M.H / (opt.side ? 3.3 : 4.2), 132);
                M.ch = M.cw * 1.4;
                el.style.setProperty('--cw', px(M.cw));
                el.style.setProperty('--gap', px(M.gap));
                el.style.setProperty('--rowgap', px(M.rowgap));
                M.ox = (M.W - n * M.cw - (n - 1) * M.gap) / 2;
                el.style.setProperty('--ox', px(M.ox));
            }
        }
        function slotCount() { return def ? def.cols + (opt.side ? 2 : 0) : 7; }

        // Where each pile stands: x in column slots, yr in rows, yk in card heights.
        function buildLayout() {
            var n = def.cols, P = [], i, side = opt.side, L = opt.lefty;
            var first = side ? 1 : 0;
            // how far the side stacks may step down without leaving the table
            var room = M.ch > 0 ? M.H / M.ch - 1 : 3;
            function col(x) { return L ? (slotCount() - 1 - x) : x; }
            for (i = 0; i < def.piles; i++) P.push({ x: 0, yr: 0, yk: 0 });
            for (i = 0; i < n; i++) {
                P[def.tab + i].x = first + i;
                P[def.tab + i].yr = side ? 0 : 1;
            }
            if (game === 'klondike') {
                if (side) {
                    P[0].x = col(0); P[1].x = col(0); P[1].yk = 1.1;
                    var ks = Math.min(1.08, room / 3);
                    for (i = 0; i < 4; i++) { P[2 + i].x = col(8); P[2 + i].yk = i * ks; }
                } else {
                    P[0].x = col(0); P[1].x = col(1);
                    for (i = 0; i < 4; i++) P[2 + i].x = L ? i : 3 + i;
                }
            } else if (game === 'freecell') {
                if (side) {
                    var fs = Math.min(1.08, room / 3);
                    for (i = 0; i < 4; i++) {
                        P[i].x = col(0); P[i].yk = i * fs;
                        P[4 + i].x = col(9); P[4 + i].yk = i * fs;
                    }
                } else {
                    for (i = 0; i < 4; i++) { P[i].x = L ? 4 + i : i; P[4 + i].x = L ? i : 4 + i; }
                }
            } else {
                if (side) {
                    P[0].x = col(0);
                    var ss = Math.min(1.08, room / 7);
                    for (i = 0; i < 8; i++) { P[1 + i].x = col(11); P[1 + i].yk = i * ss; }
                } else {
                    P[0].x = col(0);
                    for (i = 0; i < 8; i++) P[1 + i].x = L ? i : 2 + i;
                }
            }
            lay = P;
        }

        function ensureSlots() {
            var i;
            for (i = 0; i < slots.length; i++) if (slots[i].parentNode) slots[i].parentNode.removeChild(slots[i]);
            slots = [];
            var old = el.querySelectorAll('.sol-slot');
            for (i = 0; i < old.length; i++) old[i].parentNode.removeChild(old[i]);
            for (i = 0; i < def.piles; i++) {
                var s = document.createElement('div');
                s.className = 'sol-slot sol-slot-' + def.kinds[i];
                s._p = i;
                if (def.kinds[i] === 'found' && game !== 'spider') {
                    var suit = i - def.found;
                    s.appendChild(suitSvg(suit, 'sol-slot-mark'));
                    s.classList.add('sol-suit-' + SUITS[suit]);
                }
                if (def.kinds[i] === 'stock') s.appendChild(iconSvg('sol-i-again', 'sol-slot-mark'));
                el.insertBefore(s, el.firstChild);
                slots.push(s);
            }
        }
        function placeSlots() {
            for (var i = 0; i < slots.length; i++) {
                var s = slots[i].style, p = lay[i];
                s.setProperty('--x', p.x);
                s.setProperty('--yr', p.yr);
                s.setProperty('--yk', p.yk);
                slots[i]._fx = M.ox + p.x * (M.cw + M.gap);
                slots[i]._fy = p.yr * (M.ch + M.rowgap) + p.yk * M.ch;
            }
        }

        function suitSvg(suit, cls) {
            var svg = document.createElementNS(NS, 'svg');
            svg.setAttribute('viewBox', '0 0 24 24');
            svg.setAttribute('class', cls);
            svg.setAttribute('aria-hidden', 'true');
            var use = document.createElementNS(NS, 'use');
            use.setAttribute('href', '#sol-s' + suit);
            svg.appendChild(use);
            return svg;
        }
        function iconSvg(id, cls) {
            var svg = document.createElementNS(NS, 'svg');
            svg.setAttribute('viewBox', '0 0 24 24');
            svg.setAttribute('class', cls);
            svg.setAttribute('aria-hidden', 'true');
            var use = document.createElementNS(NS, 'use');
            use.setAttribute('href', '#' + id);
            svg.appendChild(use);
            return svg;
        }

        // The face of a card. The same markup is written by the blade for the
        // table the server renders — change both or neither.
        function paintFace(node, c) {
            var up = S.isUp(c), r = S.rank(c), s = S.suit(c);
            node.textContent = '';
            if (!up) {
                node.className = 'sc dn';
                node.removeAttribute('data-r');
                return;
            }
            var label = cfg.rankText(r);
            node.className = 'sc up s' + s + (s === 1 || s === 2 ? ' red' : ' blk') + (r >= 10 ? ' court' : '') + (r === 0 ? ' ace' : '') + (label.length > 1 ? ' wide' : '');
            node.setAttribute('data-r', label);
            var ix = document.createElement('span');
            ix.className = 'sc-ix';
            var b = document.createElement('b');
            b.textContent = label;
            ix.appendChild(b);
            ix.appendChild(suitSvg(s, 'sc-s'));
            node.appendChild(ix);
            if (r >= 10) {
                var big = document.createElement('em');
                big.className = 'sc-big';
                big.textContent = label;
                node.appendChild(big);
            }
            node.appendChild(suitSvg(s, 'sc-pip'));
        }
        // Paints a card the table already shows and keeps what is not the face:
        // "top" and the marks the page put on it.
        function repaint(node, c) {
            paintFace(node, c);
            node._c = c & 127;
            if (node._top) node.classList.add('top');
            if (node._run) node.classList.add('sol-run');
        }

        function nodeFor(c) {
            var id = S.uid(c), node = cards.get(id);
            if (!node) {
                node = document.createElement('div');
                node._c = -1;
                node._sig = '';
                cards.set(id, node);
                el.appendChild(node);
            }
            return node;
        }

        // ── where every card rests in a position ────────────────────────────
        // uid → {c, p, i, x, yr, yk, yd, yu, wx, fan, fx, fy, z}: fx / fy are the
        // pixels the CSS variables come to, the same numbers a tap is tested against.
        function placeAll(state) {
            var out = new Map(), P = state.piles, isSpider = game === 'spider', i, j;
            for (i = 0; i < P.length; i++) {
                var pile = P[i], kind = def.kinds[i], pos = lay[i], len = pile.length;
                var fan = kind === 'tab' ? fanFor(pile, pos) : null, nd = 0, nu = 0;
                for (j = 0; j < len; j++) {
                    var c = pile[j];
                    var x = pos.x, yr = pos.yr, yk = pos.yk, yd = 0, yu = 0, wx = 0, z = j + 1;
                    if (kind === 'tab') { yd = nd; yu = nu; if (S.isUp(c)) nu++; else nd++; }
                    else if (kind === 'waste') {
                        var show = state.v.draw === 3 ? Math.min(3, len) : 1, k = j - (len - show);
                        if (k > 0) { if (opt.side) yk += k * 0.2; else wx = k; }
                        if (opt.lefty && !opt.side && show > 1) wx -= show - 1;
                    } else if (kind === 'stock' && isSpider) {
                        var pack = Math.floor(j / 10);
                        if (opt.side) yk += pack * 0.07; else wx = pack * 0.42 * (opt.lefty ? -1 : 1);
                    } else if (kind === 'found' && isSpider) z = 14 - j;
                    out.set(S.uid(c), {
                        c: c, p: i, i: j, x: x, yr: yr, yk: yk, yd: yd, yu: yu, wx: wx, fan: fan, z: z, top: j === len - 1,
                        fx: M.ox + x * (M.cw + M.gap) + wx * M.cw * 0.28,
                        fy: yr * (M.ch + M.rowgap) + yk * M.ch + yd * (fan ? fan.fd : M.ch * 0.1) + yu * (fan ? fan.fu : M.ch * 0.27)
                    });
                }
            }
            return out;
        }

        // ── render ──────────────────────────────────────────────────────────
        // Brings the DOM in line with the state at once, then shows how it got
        // there. how.instant — nothing moves (first paint, restore, resize);
        // how.deal — the cards fly out of the stock; how.beats — the step as core.js
        // `beats` cut it (how.reverse plays it backwards: an undo); how.from — where
        // cards let go by a drag start from; how.finish — a chain of cards sent
        // home (auto-finish). Returns how long the motion takes, in ms.
        function render(state, how) {
            how = how || {};
            settle();
            st = state;
            var finalPos = placeAll(state), used = new Set(), i;
            var mode = how.instant ? 'none' : cfg.motion();
            if (motion === 'css') {
                if (how.instant || mode !== 'full') el.classList.add('sol-still');
            }
            for (i = 0; i < state.piles.length; i++) slots[i].classList.toggle('is-empty', !state.piles[i].length);
            // What the rules decided is true at once: every card knows its pile and
            // where it will rest — a tap is tested against that from this instant.
            finalPos.forEach(function (pl, id) {
                var node = nodeFor(pl.c);
                used.add(id);
                node._p = pl.p; node._i = pl.i;
                node._fx = pl.fx; node._fy = pl.fy;
                node._top = pl.top;
            });
            cards.forEach(function (node, id) {
                if (!used.has(id)) { if (node.parentNode) node.parentNode.removeChild(node); cards.delete(id); }
            });
            var prev = shown;
            shown = finalPos;
            // How it got there. A card with a flight ahead is left exactly as it is
            // shown until its flight starts: no layer, no animation, no work while it waits.
            var plan = null;
            if (motion === 'waapi' && mode === 'full' && !how.instant && (prev || how.deal)) {
                var snaps = snapshots(how, prev, finalPos);
                if (snaps) plan = choreograph(snaps, how);
            }
            finalPos.forEach(function (pl, id) {
                var node = cards.get(id), tr = plan && plan.tracks.get(id);
                if (tr) {
                    tr.pl = pl;
                    // a card new to the table (a deal) first appears where its flight starts
                    if (tr.from) { paint(node, tr.from.c); writePlace(node, tr.from); }
                    node.classList.toggle('top', pl.top);
                    return;
                }
                var turned = motion === 'css' && node._c >= 0 && !(node._c & 64) && S.isUp(pl.c) && !how.instant && !how.deal && node._c !== (pl.c & 127);
                paint(node, pl.c);
                var moved = writePlace(node, pl);
                if (motion === 'css') {
                    if (turned && !moved) flipInCss(node);
                    if (moved === 1 && !how.instant) flyCss(node, 0);
                }
                node.classList.toggle('top', pl.top);
            });
            if (curSel || pickSel) remark();
            if (motion === 'css') {
                if (el.classList.contains('sol-still')) { void el.offsetWidth; el.classList.remove('sol-still'); }
                return 0;
            }
            if (plan && plan.tracks.size) { run(plan.tracks); return plan.total; }
            if (mode === 'fade' && prev && !how.instant) return fadeIn(prev, finalPos);
            return 0;
        }
        function paint(node, c) {
            if (node._c !== (c & 127)) repaint(node, c);
        }
        // The CSS place of a card. Returns 0 — unchanged, 1 — moved, 2 — placed for the first time.
        function writePlace(node, pl) {
            var sty = node.style, fan = pl.fan, out = 0;
            var sig = pl.x + '|' + pl.yr + '|' + pl.yk + '|' + pl.yd + '|' + pl.yu + '|' + pl.wx + '|' + (fan ? fan.fd + '/' + fan.fu : '');
            if (node._sig !== sig) {
                out = node._sig === '' ? 2 : 1;
                node._sig = sig;
                sty.setProperty('--x', pl.x);
                sty.setProperty('--yr', pl.yr);
                sty.setProperty('--yk', pl.yk);
                sty.setProperty('--yd', pl.yd);
                sty.setProperty('--yu', pl.yu);
                sty.setProperty('--wx', pl.wx);
                if (fan && fan.tight) { sty.setProperty('--fd', px(fan.fd)); sty.setProperty('--fu', px(fan.fu)); }
                else { sty.removeProperty('--fd'); sty.removeProperty('--fu'); }
            }
            if (node._z !== pl.z) { node._z = pl.z; sty.setProperty('--z', pl.z); }
            return out;
        }

        // The positions a step passes through, as resting places.
        function snapshots(how, prev, finalPos) {
            var list = [], i;
            if (how.deal) {
                // every card of the deal starts in the stock (FreeCell: on the first cell), face down
                var from = lay[0], start = new Map();
                finalPos.forEach(function (pl, id) {
                    var dealt = pl.p >= def.tab, up = game === 'freecell';
                    start.set(id, !dealt ? pl : {
                        c: up ? pl.c : (pl.c & ~S.UP), p: -1, i: pl.i, z: 1,
                        x: from.x, yr: from.yr, yk: from.yk, yd: 0, yu: 0, wx: 0, fan: null,
                        fx: M.ox + from.x * (M.cw + M.gap), fy: from.yr * (M.ch + M.rowgap) + from.yk * M.ch
                    });
                });
                return [{ pos: start }, { pos: finalPos, kind: 'dealall' }];
            }
            if (how.beats && how.beats.length > 1) {
                for (i = 0; i < how.beats.length; i++) list.push({ pos: placeAll(how.beats[i].st), kind: how.beats[i].kind });
                if (how.reverse) {
                    // backwards: the beat that happened last is taken back first, under its own name
                    var back = [];
                    for (i = list.length - 1; i >= 0; i--) back.push({ pos: list[i].pos, kind: i + 1 < list.length ? list[i + 1].kind : 'start', rev: true });
                    list = back;
                }
                list[list.length - 1].pos = finalPos;
                // the table had these cards where it last drew them: start from there
                list[0].pos = mergeShown(list[0].pos, prev);
                return list;
            }
            return [{ pos: prev }, { pos: finalPos, kind: how.kind || 'move' }];
        }
        function mergeShown(pos, prev) {
            if (!prev) return pos;
            var out = new Map();
            pos.forEach(function (pl, id) {
                var was = prev.get(id);
                out.set(id, was && (was.c & 127) === (pl.c & 127) ? was : pl);
            });
            return out;
        }

        // ── the choreography ────────────────────────────────────────────────
        // Each card that moves gets a track: segments with a start time, from
        // where to where, and whether it turns over on the way. Segments run as
        // Web Animations, one after another; a card waiting for its turn keeps
        // its old place through the animation's backward fill (no timer holds it).
        function choreograph(snaps, how) {
            var base = Math.max(40, cfg.speedMs() || 220), L = light;
            var flipDur = base * (L ? 0.55 : 0.85);
            var tracks = new Map(), cardsLast = new Map(), lift = 0, t = 0, firstStart = 0, total = 0;
            var cap = L ? CAP_LIGHT : CAP;
            var from = how.from || null;
            function track(id) {
                var tr = tracks.get(id);
                if (!tr) {
                    var node = cards.get(id);
                    if (!node) return null;
                    var s0 = snaps[0].pos.get(id);
                    tr = { id: id, node: node, segs: [], z0: s0 ? s0.z : (node._z || 1), c0: s0 ? s0.c : node._c, anims: [], timers: [], done: false, started: false,
                        from: node._sig === '' && s0 && s0.x != null ? s0 : null };
                    tracks.set(id, tr);
                }
                return tr;
            }
            function dist(a, b) { return Math.sqrt((a.fx - b.fx) * (a.fx - b.fx) + (a.fy - b.fy) * (a.fy - b.fy)); }
            function flightMs(a, b) { return base * (L ? 0.8 : 1) * (0.85 + 0.4 * Math.min(1, dist(a, b) / (3.5 * Math.max(20, M.ch)))); }

            for (var k = 1; k < snaps.length; k++) {
                var A = snaps[k - 1].pos, B = snaps[k].pos, kind = snaps[k].kind || 'move', rev = !!snaps[k].rev;
                var movers = [], shifts = [], flipsUp = [], flipsDown = [];
                B.forEach(function (b, id) {
                    var a = A.get(id);
                    if (!a) return;
                    var moved = Math.abs(a.fx - b.fx) + Math.abs(a.fy - b.fy) > 0.5;
                    var turned = S.isUp(a.c) !== S.isUp(b.c);
                    var changedPile = a.p !== b.p || a.i !== b.i;
                    if (moved && (changedPile || kind === 'dealall')) movers.push({ id: id, a: a, b: b, turn: turned });
                    else if (moved) shifts.push({ id: id, a: a, b: b, turn: turned });
                    else if (turned) (S.isUp(b.c) ? flipsUp : flipsDown).push({ id: id, a: a, b: b });
                });
                if (!movers.length && !shifts.length && !flipsUp.length && !flipsDown.length) continue;

                // order of the movers: the order a hand would lay them down
                if (kind === 'collect' && !rev) movers.sort(function (x, y) { return y.b.i - x.b.i; });            // ace first
                else if (kind === 'collect') movers.sort(function (x, y) { return x.b.i - y.b.i; });             // king back first
                else if (kind === 'deal') movers.sort(function (x, y) { return rev ? (x.a.p - y.a.p) : (x.b.p - y.b.p); });   // column by column
                else if (kind === 'dealall') movers.sort(function (x, y) { return (x.b.i - y.b.i) || (x.b.p - y.b.p); });     // row by row
                // the waste goes back as one pile: the card on top of it flies last, highest, and turns over in the air
                else if (kind === 'recycle') movers.sort(function (x, y) { return rev ? x.b.i - y.b.i : x.a.i - y.a.i; });
                else movers.sort(function (x, y) { return (x.b.p - y.b.p) || (x.b.i - y.b.i); });

                var stagger = 16, durK = 1, arc = 0;
                if (kind === 'draw') { stagger = 70; durK = 1.15; }
                else if (kind === 'deal') { stagger = L ? 30 : 55; durK = 1.25; }
                else if (kind === 'dealall') { stagger = 14; durK = 1.2; }
                else if (kind === 'collect') { stagger = L ? 22 : 42; durK = 1.15; arc = L ? 0 : M.ch * 0.45; }
                else if (kind === 'recycle') { stagger = 0; durK = 1.15; }
                else if (movers.length > 1) stagger = Math.min(16, 90 / movers.length);
                if (rev) durK *= 0.85;
                // never more than `cap` cards in the air: a long list is spread out
                if (movers.length > cap) stagger = Math.max(stagger, (base * durK) / cap);

                // when this beat starts: after the last one — or, in a chain of cards
                // sent home, a little after the last one started
                var start = t;
                if (how.finish && k > 1) start = Math.max(0, firstStart + base * (L ? 0.3 : 0.42));
                // a card is never asked to be in two places: its next flight waits for its last
                movers.concat(shifts).forEach(function (m) { var e = cardsLast.get(m.id); if (e != null && e + 10 > start) start = e + 10; });
                firstStart = start;
                var tt = start, end = start;
                if (kind === 'collect' && !rev && !L) {
                    // the finished suit lights up where it lies, then leaves
                    movers.forEach(function (m) { var tr = track(m.id); if (tr) tr.glow = tt; });
                    tt += 260;
                }
                // a card turning face down where it lies (an undo) does it first
                flipsDown.forEach(function (f) {
                    var tr = track(f.id);
                    if (!tr) return;
                    tr.segs.push({ t: tt, dur: flipDur, a: f.a, b: f.b, flip: 'place', c: f.b.c, z: null });
                    end = Math.max(end, tt + flipDur);
                    cardsLast.set(f.id, tt + flipDur);
                });
                if (flipsDown.length) tt += flipDur;
                var landed = tt;
                movers.forEach(function (m, n) {
                    var tr = track(m.id);
                    if (!tr) return;
                    var a = m.a;
                    // a card let go by a drag starts where the finger left it
                    if (from && k === 1 && from.has(m.id)) a = { fx: from.get(m.id).x, fy: from.get(m.id).y, c: a.c };
                    var d = flightMs(a, m.b) * durK, s0 = tt + n * stagger;
                    var flip = m.turn ? (L || (kind === 'recycle' && n !== movers.length - 1) ? 'swap' : 'mid') : null;
                    var home = def.kinds[m.b.p] === 'found';
                    tr.segs.push({ t: s0, dur: d, a: a, b: m.b, flip: flip, c: m.b.c, z: 2000 + (++lift), arc: arc, home: home && !L });
                    landed = Math.max(landed, s0 + d);
                    cardsLast.set(m.id, s0 + d);
                });
                shifts.forEach(function (m) {
                    var tr = track(m.id);
                    if (!tr) return;
                    var d = base * (L ? 0.8 : 1);
                    tr.segs.push({ t: tt, dur: d, a: m.a, b: m.b, flip: m.turn ? 'swap' : null, c: m.b.c, z: null });
                    landed = Math.max(landed, tt + d);
                    cardsLast.set(m.id, tt + d);
                });
                end = Math.max(end, landed);
                // a card a move uncovered turns over once the move has landed
                flipsUp.forEach(function (f) {
                    var tr = track(f.id);
                    if (!tr) return;
                    var s1 = Math.max(landed, cardsLast.get(f.id) || 0);
                    tr.segs.push({ t: s1, dur: flipDur, a: f.a, b: f.b, flip: L ? 'swap' : 'place', c: f.b.c, z: null });
                    end = Math.max(end, s1 + (L ? 0 : flipDur));
                    cardsLast.set(f.id, s1 + flipDur);
                });
                t = end + (L ? 20 : 40);
                total = Math.max(total, end);
            }
            return { tracks: tracks, total: Math.round(total) };
        }

        // Plays the tracks. Everything here can be cut short by settle().
        function run(tracks) {
            var me = { id: ++seqNo, t0: now(), tracks: tracks, left: 0, frames: [] };
            seq = me;
            tracks.forEach(function (tr) {
                var node = tr.node;
                tr.cFinal = tr.pl ? tr.pl.c & 127 : node._c;     // the face after the step
                if (!tr.segs.length) { tr.done = true; finalize(tr); return; }
                me.left++;
                // a card that turns over on the way shows its old face until then
                if ((tr.c0 & 127) !== node._c) repaint(node, tr.c0);
                if (tr.glow != null) {
                    tr.timers.push(setTimeout(function () { if (seq === me) { node._run = true; node.classList.add('sol-run'); } }, Math.max(0, tr.glow - (now() - me.t0))));
                }
                // its first flight starts on time; until then the card is simply where it was
                var wait = tr.segs[0].t - (now() - me.t0);
                if (wait <= 1) step(me, tr, 0);
                else tr.timers.push(setTimeout(function () { step(me, tr, 0); }, wait));
            });
            if (!me.left) { seq = null; flushIdle(); return; }
        }
        function step(me, tr, k) {
            if (seq !== me) return;
            var seg = tr.segs[k], node = tr.node;
            if (!seg) { finishTrack(me, tr); return; }
            if (!tr.started) {
                // the card leaves its old place now: its CSS place becomes the new one and
                // the animation carries it there from where it was drawn
                tr.started = true;
                if (tr.pl) writePlace(node, tr.pl);
                node.style.zIndex = String(seg.z != null ? seg.z : tr.z0);
            }
            var late = now() - me.t0 - seg.t;
            // How late each card leaves against the plan: the meter of a machine that cannot
            // keep up (a frame loop to count frames would itself wake the page 60 times a second).
            if (k > 0 || tr.segs[0].t > 0) me.frames.push(Math.max(0, late));
            var delay = Math.max(0, -late);
            var a = seg.a, b = seg.b, timing = seg.home ? 'cubic-bezier(.3,1.25,.55,1)' : 'cubic-bezier(.22,.8,.28,1)';
            function lifted() {
                if (seg.z == null) return;
                if (delay < 2) node.style.zIndex = String(seg.z);
                else tr.timers.push(setTimeout(function () { if (seq === me) node.style.zIndex = String(seg.z); }, delay));
            }
            function next() { step(me, tr, k + 1); }
            function keep(an, then) {
                if (!an) { then(); return; }
                tr.anims.push(an);
                an.onfinish = function () { if (seq === me) then(); };
            }
            if (seg.flip === 'place' || seg.flip === 'mid') {
                // turned over in the air or where it lies: edge-on at the half, the new face after it
                var mx = (a.fx + b.fx) / 2, my = (a.fy + b.fy) / 2 - (seg.arc || 0);
                var half = seg.dur / 2, pop = seg.flip === 'place' ? ' scale(1.06)' : '';
                lifted();
                keep(anim(node, [
                    { transform: at(a.fx, a.fy) + ' perspective(700px) rotateY(0deg)' },
                    { transform: at(mx, my) + ' perspective(700px) rotateY(90deg)' + pop }
                ], { duration: half, delay: delay, easing: 'ease-in', fill: 'both' }), function () {
                    repaint(node, seg.c);
                    remark();
                    keep(anim(node, [
                        { transform: at(mx, my) + ' perspective(700px) rotateY(-90deg)' + pop },
                        { transform: at(b.fx, b.fy) + ' perspective(700px) rotateY(0deg)' }
                    ], { duration: half, easing: seg.home ? timing : 'ease-out', fill: 'both' }), next);
                });
                return;
            }
            if (seg.flip === 'swap') tr.timers.push(setTimeout(function () { if (seq === me) { repaint(node, seg.c); remark(); } }, delay));
            lifted();
            if (Math.abs(a.fx - b.fx) + Math.abs(a.fy - b.fy) < 0.5) {
                // nothing to travel (a face swapped where it lies): wait the segment out
                tr.timers.push(setTimeout(function () { if (seq === me) next(); }, delay + seg.dur));
                return;
            }
            var frames;
            if (seg.arc) {
                // the top of the arc stays on the table: a flight to a home in the top row does not cross the bar above it
                var hx = a.fx + (b.fx - a.fx) * 0.45, hy = Math.max(0, Math.min(a.fy, b.fy) - seg.arc);
                frames = [{ transform: at(a.fx, a.fy), easing: 'ease-in' }, { transform: at(hx, hy), easing: seg.home ? 'cubic-bezier(.3,1.2,.55,1)' : 'ease-out', offset: 0.45 }, { transform: at(b.fx, b.fy) }];
                timing = 'linear';
            } else frames = [{ transform: at(a.fx, a.fy) }, { transform: at(b.fx, b.fy) }];
            keep(anim(node, frames, { duration: seg.dur, delay: delay, easing: timing, fill: 'both' }), next);
        }
        // A card that has landed keeps its finished animation (it holds the card exactly
        // where its CSS place is) until a batch of them is released together: every
        // release is a layer taken down and a repaint, and one of those for twelve cards
        // costs the main thread far less than twelve, one per landing.
        function finishTrack(me, tr) {
            if (tr.done) return;
            tr.done = true;
            (me.parked || (me.parked = [])).push(tr);
            if (me.parked.length >= 12) unpark(me);
            if (--me.left <= 0 && seq === me) endSeq(me, true);
        }
        function unpark(me) {
            var list = me.parked || [];
            me.parked = [];
            for (var i = 0; i < list.length; i++) release(list[i]);
        }
        // A track is over: the card rests on its CSS place, its own face, its own height.
        function release(tr) {
            var node = tr.node, i;
            for (i = 0; i < tr.anims.length; i++) { try { tr.anims[i].cancel(); } catch (_) {} }
            for (i = 0; i < tr.timers.length; i++) clearTimeout(tr.timers[i]);
            tr.anims = []; tr.timers = [];
            node.style.zIndex = '';
            if (node._run) { node._run = false; node.classList.remove('sol-run'); }
            finalize(tr);
        }
        function finalize(tr) {
            var node = tr.node;
            if (cards.get(tr.id) !== node) return;
            if (!tr.started) { tr.started = true; if (tr.pl) writePlace(node, tr.pl); }
            if (tr.cFinal != null && node._c !== tr.cFinal) repaint(node, tr.cFinal);
        }
        function endSeq(me, natural) {
            if (seq === me) seq = null;
            unpark(me);
            if (natural) judgeFrames(me.frames);
            if (root.__solMotionTrace) root.__solMotionTrace.push({ frames: me.frames.slice(), natural: natural, light: light });
            remark();
            flushIdle();
        }
        // Every flight finished this instant: the table is what the state says.
        function settle() {
            var me = seq;
            if (!me) return;
            seq = null;
            me.tracks.forEach(function (tr) { if (!tr.done) { tr.done = true; release(tr); } });
            endSeq(me, false);
        }
        function flushIdle() {
            if (seq) return;
            var q = idleQ;
            idleQ = [];
            for (var i = 0; i < q.length; i++) { try { q[i](); } catch (_) {} }
        }
        // More than a third of the cards leaving three frames or more behind the plan,
        // twice: light motion for the rest of the visit (shorter, no turning in the air,
        // fewer cards at once, the calm finish). A desktop busy with other work starts a
        // card a frame or two late now and then — that is not the machine to slow down for.
        function judgeFrames(fr) {
            if (light || fr.length < 6) return;
            var late = 0;
            for (var i = 0; i < fr.length; i++) if (fr[i] > 50) late++;
            if (late / fr.length > 1 / 3) {
                if (++slowRuns >= 2) { light = true; if (cfg.onLight) cfg.onLight({ late: Math.round(late / fr.length * 100), starts: fr.length }); }
            } else if (slowRuns > 0) slowRuns--;
        }

        // Reduced motion: no travel — the cards that changed place fade in where they rest.
        function fadeIn(prev, next) {
            var list = [];
            next.forEach(function (pl, id) {
                var was = prev && prev.get(id);
                if (!was || Math.abs(was.fx - pl.fx) + Math.abs(was.fy - pl.fy) > 0.5 || (was.c & 127) !== (pl.c & 127)) {
                    var node = cards.get(id);
                    if (node) list.push(node);
                }
            });
            if (list.length > 40) return 0;
            list.forEach(function (node) { anim(node, [{ opacity: 0.25 }, { opacity: 1 }], { duration: 180, easing: 'ease-out' }); });
            return list.length ? 180 : 0;
        }

        // A column that would run off the table closes up its fan: face-down
        // cards first (nothing to read there), then the face-up ones.
        function fanFor(pile, pos) {
            var nd = S.down(pile), nu = pile.length - nd;
            var fd = M.ch * 0.1, fu = M.ch * (opt.xl ? 0.33 : 0.27);
            var top = pos.yr * (M.ch + M.rowgap) + pos.yk * M.ch;
            var avail = M.H - top - M.ch - 2;
            var need = nd * fd + Math.max(0, nu - 1) * fu;
            if (need <= avail || avail <= 0) return { fd: fd, fu: fu, tight: false };
            var fdMin = Math.max(2, M.ch * 0.035);
            if (nd) fd = Math.max(fdMin, Math.min(fd, (avail - Math.max(0, nu - 1) * fu) / nd));
            if (nu > 1) fu = Math.max(3, Math.min(fu, (avail - nd * fd) / (nu - 1)));
            return { fd: fd, fu: fu, tight: true };
        }

        // Without Web Animations: a card on the move rides above the table until it has landed.
        function flyCss(node, delay) {
            node.classList.add('fly');
            var tm = flyTimer.get(node);
            if (tm) clearTimeout(tm);
            flyTimer.set(node, setTimeout(function () {
                node.classList.remove('fly');
                flyTimer.delete(node);
            }, (delay || 0) + cfg.speedMs() + 80));
        }
        function flipInCss(node) {
            node.classList.remove('flip');
            void node.offsetWidth;
            node.classList.add('flip');
            setTimeout(function () { node.classList.remove('flip'); }, 260);
        }

        function relayout() {
            if (!def || !st) return;
            settle();
            measure();
            buildLayout();
            measure();          // the side stacks change the card size; read it again
            buildLayout();
            placeSlots();
            cards.forEach(function (node) { node._sig = ''; });
            render(st, { instant: true });
        }

        function setGame(g, state) {
            settle();
            clearHint();
            hideTip();
            game = g; def = S.games[g]; st = state;
            shown = null;
            el.setAttribute('data-game', g);
            // cards of another game leave the table
            cards.forEach(function (node) { if (node.parentNode) node.parentNode.removeChild(node); });
            cards.clear();
            var stale = el.querySelectorAll('.sc');
            for (var i = 0; i < stale.length; i++) stale[i].parentNode.removeChild(stale[i]);
            ensureSlots();
            measure(); buildLayout(); measure(); buildLayout();
            placeSlots();
        }

        // ── gestures ────────────────────────────────────────────────────────
        // What lies under a point is decided by where the cards come to REST, not
        // by where an animation has them this frame: a second tap on the stock
        // must reach the stock even while the card just turned is still leaving it.
        // The table's box is read at the press, when the layout is clean; the click of
        // the same press reuses it instead of forcing a layout after whatever the
        // press changed (a press during a flight lands every card).
        var pressRect = null, pressAt = 0;
        function hit(e, rect) {
            var r = rect || el.getBoundingClientRect(), x = e.clientX - r.left, y = e.clientY - r.top;
            var P = st.piles, found = null, i, j;
            for (i = 0; i < P.length; i++) {
                for (j = 0; j < P[i].length; j++) {
                    var node = cards.get(S.uid(P[i][j]));
                    if (node && x >= node._fx && x <= node._fx + M.cw && y >= node._fy && y <= node._fy + M.ch) found = node;
                }
            }
            if (found) return { node: found, slot: null };
            for (i = 0; i < slots.length; i++) {
                var s = slots[i];
                if (x >= s._fx && x <= s._fx + M.cw && y >= s._fy && y <= s._fy + M.ch) return { node: null, slot: s };
            }
            return { node: null, slot: null };
        }
        function cardAt(e) { return st ? hit(e).node : null; }
        function runOf(node) {
            var pile = st.piles[node._p], out = [];
            for (var j = node._i; j < pile.length; j++) out.push(cards.get(S.uid(pile[j])));
            return out;
        }

        var lastPointer = 'mouse';
        function onDown(e) {
            dragClick = false;
            lastPointer = e.pointerType || 'mouse';
            if (drag) endDrag(false);
            if (e.pointerType === 'mouse' && e.button !== 0) return;
            // a press on the bar or the overlay inside the table is theirs, not a card's
            if (e.target !== el && !(e.target.closest && (e.target.closest('.sc') || e.target.closest('.sol-slot')))) return;
            // what lies under the finger is read first (the layout is clean now), then
            // whatever is still in the air lands this instant
            pressRect = el.getBoundingClientRect(); pressAt = e.timeStamp || 0;
            var node = st ? hit(e, pressRect).node : null;
            settle();
            if (!node || !st) return;
            var n = st.piles[node._p].length - node._i;
            if (!cfg.canLift(node._p, n)) return;
            drag = {
                id: e.pointerId, p: node._p, n: n, node: node, els: null,
                x0: e.clientX, y0: e.clientY, dx: 0, dy: 0, far: 0,
                started: false, slop: e.pointerType === 'mouse' ? 4 : 8, targets: null, hot: -1
            };
        }
        function onMove(e) {
            if (!drag || e.pointerId !== drag.id) return;
            drag.dx = e.clientX - drag.x0; drag.dy = e.clientY - drag.y0;
            var dist = Math.abs(drag.dx) + Math.abs(drag.dy);
            if (dist > drag.far) drag.far = dist;
            if (!drag.started) {
                if (Math.abs(drag.dx) < drag.slop && Math.abs(drag.dy) < drag.slop) return;
                startDrag(e);
            }
            if (!raf) raf = requestAnimationFrame(paintDrag);
        }
        function startDrag(e) {
            drag.started = true;
            drag.els = runOf(drag.node);
            try { el.setPointerCapture(e.pointerId); } catch (_) {}
            el.classList.add('sol-dragging');
            clearHint();
            hideTip();
            for (var i = 0; i < drag.els.length; i++) drag.els[i].classList.add('drag');
            drag.targets = cfg.targets(drag.p, drag.n);
            markTargets(drag.targets, true);
            if (cfg.onDragStart) cfg.onDragStart(drag.p, drag.n);
        }
        // The run under the finger: its own transform, written once a frame — no
        // custom property the whole card (and its children) would recompute.
        function paintDrag() {
            raf = 0;
            if (!drag || !drag.started) return;
            for (var i = 0; i < drag.els.length; i++) {
                var n = drag.els[i];
                n.style.transform = at(n._fx + drag.dx, n._fy + drag.dy);
            }
            var hot = dropTarget();
            if (hot !== drag.hot) {
                if (drag.hot >= 0) { var o = topRef(drag.hot); if (o) o.classList.remove('tgt-hot'); }
                drag.hot = hot;
                if (hot >= 0) { var h = topRef(hot); if (h) h.classList.add('tgt-hot'); }
            }
        }
        function markTargets(list, on) {
            if (!list) return;
            for (var i = 0; i < list.length; i++) {
                var node = topRef(list[i]);
                if (node) { node.classList.toggle('tgt', on); if (!on) node.classList.remove('tgt-hot'); }
            }
        }
        // How much of the dragged card covers pile t, in table pixels (resting places, no layout reads).
        function cover(d, t) {
            var x = d.node._fx + d.dx, y = d.node._fy + d.dy, pile = st.piles[t];
            var ref = pile.length ? cards.get(S.uid(pile[pile.length - 1])) : slots[t];
            if (!ref) return 0;
            var rx = ref._fx, top = ref._fy, bottom = ref._fy + M.ch;
            // a tableau column counts all the way down, so a card dropped below a short column still lands on it
            if (def.kinds[t] === 'tab') { bottom = M.H; if (pile.length) top = slots[t]._fy; }
            var w = Math.min(x + M.cw, rx + M.cw) - Math.max(x, rx);
            var h = Math.min(y + M.ch, bottom) - Math.max(y, top);
            return w > 0 && h > 0 ? w * h : 0;
        }
        // Where the dragged run was let go: the allowed pile it covers most.
        function dropTarget() {
            var best = -1, bestArea = 0, i;
            for (i = 0; i < drag.targets.length; i++) {
                var area = cover(drag, drag.targets[i]);
                if (area > bestArea) { bestArea = area; best = drag.targets[i]; }
            }
            // A card let go over the wrong foundation still goes home: the
            // foundations are one place to the player, four to the rules.
            if (best < 0) {
                var home = -1;
                for (i = 0; i < drag.targets.length; i++) if (def.kinds[drag.targets[i]] === 'found') home = drag.targets[i];
                if (home >= 0) for (i = 0; i < slots.length; i++) if (def.kinds[i] === 'found' && overSlot(i)) return home;
            }
            return best;
        }
        function overSlot(i) {
            var x = drag.node._fx + drag.dx, y = drag.node._fy + drag.dy, s = slots[i];
            return Math.min(x + M.cw, s._fx + M.cw) - Math.max(x, s._fx) > 0 && Math.min(y + M.ch, s._fy + M.ch) - Math.max(y, s._fy) > 0;
        }
        // The pile the run was let go over, allowed or not — what a refusal is about.
        function overPile(d) {
            var best = -1, bestArea = 0;
            for (var t = 0; t < def.piles; t++) {
                if (t === d.p) continue;
                var area = cover(d, t);
                if (area > bestArea) { bestArea = area; best = t; }
            }
            // a third of a card at least: brushing a pile on the way back is not a drop on it
            return bestArea > M.cw * M.ch / 3 ? best : -1;
        }
        function endDrag(drop) {
            var d = drag;
            if (raf) { cancelAnimationFrame(raf); raf = 0; }
            if (!d || !d.started) { drag = null; return; }
            el.classList.remove('sol-dragging');
            markTargets(d.targets, false);
            var to = -1, over = -1;
            if (drop) { to = dropTarget(); if (to < 0) over = overPile(d); }
            drag = null;
            // where the run was let go, in table pixels: the flight home or onward starts there
            var from = new Map(), i;
            for (i = 0; i < d.els.length; i++) {
                var n = d.els[i];
                from.set(S.uid(st.piles[n._p][n._i]), { x: n._fx + d.dx, y: n._fy + d.dy });
                n.classList.remove('drag');
                n.style.transform = '';
            }
            var done = to >= 0 && cfg.onDrop(d.p, d.n, to, from);
            if (!done) flyBack(d.els, from, over >= 0);
            // The browser may still send this press's click. It is the drag's,
            // not a tap — unless the "drag" was a drifting tap that went nowhere.
            dragClick = done || d.far > 24;
            if (cfg.onDragEnd) cfg.onDragEnd(done, { p: d.p, n: d.n, over: over });
        }
        // A run let go where it may not go flies back to its place (and shakes once, if it was refused).
        function flyBack(els, from, refused) {
            if (motion === 'css' || cfg.motion() !== 'full') {
                for (var i = 0; i < els.length; i++) flyCss(els[i], 0);
                if (refused) shakeEls(els);
                return;
            }
            settle();
            var tracks = new Map(), base = Math.max(40, cfg.speedMs() || 220);
            els.forEach(function (n, j) {
                var id = S.uid(st.piles[n._p][n._i]), f = from.get(id);
                if (!f) return;
                tracks.set(id, { id: id, node: n, z0: 3000 + j, c0: n._c, anims: [], timers: [], done: false, started: false, pl: null, segs: [{ t: j * 10, dur: base * 0.9, a: { fx: f.x, fy: f.y }, b: { fx: n._fx, fy: n._fy }, flip: null, c: n._c, z: null }] });
            });
            if (!tracks.size) return;
            if (refused) idleQ.push(function () { shakeEls(els); });
            run(tracks);
        }
        function onUp(e) {
            if (!drag || e.pointerId !== drag.id) return;
            if (!drag.started) { drag = null; return; }
            drag.dx = e.clientX - drag.x0; drag.dy = e.clientY - drag.y0;
            endDrag(true);
        }
        function onCancel(e) {
            if (!drag || e.pointerId !== drag.id) return;
            endDrag(false);
        }
        function onClick(e) {
            // Only a pointer's click can belong to a drag; a keyboard click (detail 0) is always meant.
            if (dragClick && e.detail !== 0) { dragClick = false; return; }
            dragClick = false;
            if (!st) return;
            // a click on the bar or the overlay inside the table is theirs, not the table's
            if (e.target !== el && !(e.target.closest && (e.target.closest('.sc') || e.target.closest('.sol-slot')))) return;
            var fresh = e.detail !== 0 && pressRect && (e.timeStamp || 0) - pressAt < 2000;
            var hitAt = hit(e, fresh ? pressRect : null);
            if (hitAt.node) { cfg.onTap(hitAt.node._p, st.piles[hitAt.node._p].length - hitAt.node._i, e); return; }
            if (hitAt.slot) { cfg.onSlot(hitAt.slot._p, e); return; }
            if (cfg.onFelt) cfg.onFelt(e);
        }

        el.addEventListener('pointerdown', onDown);
        el.addEventListener('pointermove', onMove);
        el.addEventListener('pointerup', onUp);
        el.addEventListener('pointercancel', onCancel);
        el.addEventListener('click', onClick);
        // touch-action:none stops the scroll, not the browser's fling: a card let
        // go on the move leaves one, and the next tap would get no click.
        el.addEventListener('touchmove', function (e) {
            if (drag && drag.started && e.cancelable) e.preventDefault();
        }, { passive: false });
        el.addEventListener('contextmenu', function (e) {
            if (!cfg.onContext) return;
            e.preventDefault();
            // Android sends contextmenu for a long press: that is not a right click.
            if ((e.pointerType || lastPointer) !== 'mouse') return;
            cfg.onContext(e);
        });

        // ── marks: hint, keyboard cursor, picked-up run ─────────────────────
        function clearHint() {
            for (var i = 0; i < hintEls.length; i++) hintEls[i].classList.remove('hint-from', 'hint-to');
            hintEls = [];
            for (i = 0; i < ghosts.length; i++) if (ghosts[i].parentNode) ghosts[i].parentNode.removeChild(ghosts[i]);
            ghosts = [];
        }
        function topRef(p) {
            var pile = st.piles[p];
            return pile.length ? cards.get(S.uid(pile[pile.length - 1])) : slots[p];
        }
        // The hint shows both ends: the cards to move light up, the place they
        // go is marked, and a see-through copy of them travels there — twice.
        function showHint(mv) {
            settle();
            clearHint();
            if (!st) return;
            var i, node, srcNodes = [];
            if (mv.k !== 'm') {
                node = st.piles[0].length ? topRef(0) : slots[0];
                if (node) { node.classList.add('hint-from'); hintEls.push(node); }
            } else {
                var pile = st.piles[mv.f];
                for (i = pile.length - mv.n; i < pile.length; i++) {
                    node = cards.get(S.uid(pile[i]));
                    if (node) { node.classList.add('hint-from'); hintEls.push(node); srcNodes.push(node); }
                }
                node = topRef(mv.t);
                if (node) { node.classList.add('hint-to'); hintEls.push(node); }
            }
            if (motion !== 'waapi' || cfg.motion() !== 'full' || light) return;
            // where the cards would land: the same resting places as a real move
            var sim = S.clone(st), list = [];
            try { if (!def.legal(sim, mv)) return; def.apply(sim, mv); } catch (_) { return; }
            var after = placeAll(sim);
            if (mv.k === 'm') {
                srcNodes.forEach(function (n) { var id = S.uid(st.piles[n._p][n._i]); list.push({ n: n, b: after.get(id) }); });
            } else if (mv.k === 'r') {
                var w = st.piles[1];
                if (w.length) list.push({ n: cards.get(S.uid(w[w.length - 1])), b: after.get(S.uid(w[w.length - 1])), back: true });
            } else {
                // the stock: the cards it would turn or deal
                var stock = st.piles[0], cnt = game === 'spider' ? Math.min(10, stock.length) : Math.min(st.v.draw, stock.length);
                for (i = 0; i < cnt; i++) { var c = stock[stock.length - 1 - i]; list.push({ n: cards.get(S.uid(c)), b: after.get(S.uid(c)), back: true }); }
            }
            list.forEach(function (it, j) {
                if (!it.n || !it.b) return;
                var g = it.n.cloneNode(true);
                g.className = (it.back ? 'sc dn' : it.n.className.replace(/\b(top|hint-from|hint-to|cur|picked|tgt|tgt-hot|drag|fly|nope)\b/g, '')) + ' sc-ghost';
                g.removeAttribute('data-early-action');
                g.setAttribute('aria-hidden', 'true');
                if (it.back) g.textContent = '';
                el.appendChild(g);
                ghosts.push(g);
                var a = at(it.n._fx, it.n._fy), b = at(it.b.fx, it.b.fy);
                var an = anim(g, [
                    { transform: a, opacity: 0 },
                    { transform: a, opacity: 0.9, offset: 0.12, easing: 'cubic-bezier(.45,0,.25,1)' },
                    { transform: b, opacity: 0.9, offset: 0.6 },
                    { transform: b, opacity: 0, offset: 0.85 },
                    { transform: b, opacity: 0 }
                ], { duration: 1500, delay: j * 40, iterations: 2, fill: 'both' });
                if (an) an.onfinish = function () { if (g.parentNode) g.parentNode.removeChild(g); };
                else if (g.parentNode) g.parentNode.removeChild(g);
            });
        }
        function shake(p, n) {
            var pile = st.piles[p], list = [];
            for (var i = Math.max(0, pile.length - n); i < pile.length; i++) list.push(cards.get(S.uid(pile[i])));
            if (!list.length && slots[p]) list.push(slots[p]);
            shakeEls(list);
        }
        function shakeEls(list) {
            list.forEach(function (node) {
                if (!node) return;
                node.classList.remove('nope');
                void node.offsetWidth;
                node.classList.add('nope');
                setTimeout(function () { node.classList.remove('nope'); }, 360);
            });
        }

        // A short line next to a card: why it did not move. Above the card when
        // there is room, inside the table always; it never takes a press.
        var tipEl = null, tipTimer = 0;
        function tip(p, n, content, ms) {
            if (!st) return;
            if (!tipEl) {
                tipEl = document.createElement('div');
                tipEl.className = 'sol-why';
                tipEl.setAttribute('aria-hidden', 'true');
                el.appendChild(tipEl);
            }
            clearTimeout(tipTimer);
            tipEl.textContent = '';
            tipEl.appendChild(content);
            tipEl.hidden = false;
            var pile = st.piles[p] || [], ref = pile.length ? cards.get(S.uid(pile[Math.max(0, pile.length - n)])) : slots[p];
            var x = ref ? ref._fx + M.cw / 2 : M.W / 2, y = ref ? ref._fy : M.H / 2;
            var bw = tipEl.offsetWidth, bh = tipEl.offsetHeight;
            var left = Math.max(4, Math.min(M.W - bw - 4, x - bw / 2));
            var top = y - bh - 8 >= 2 ? y - bh - 8 : Math.min(M.H - bh - 4, y + Math.min(M.ch * 0.5, 30));
            tipEl.style.transform = at(left, top);
            anim(tipEl, [{ opacity: 0, transform: at(left, top + 6) }, { opacity: 1, transform: at(left, top) }], { duration: 160, easing: 'ease-out' });
            tipTimer = setTimeout(hideTip, ms || 2800);
        }
        function hideTip() {
            clearTimeout(tipTimer);
            if (!tipEl || tipEl.hidden) return;
            var t = tipEl;
            var an = anim(t, [{ opacity: 1 }, { opacity: 0 }], { duration: 200, easing: 'ease-in', fill: 'forwards' });
            if (an) an.onfinish = function () { t.hidden = true; try { an.cancel(); } catch (_) {} };
            else t.hidden = true;
        }

        var curSel = null, pickSel = null;
        // A card repainted by render (turned over, rank letters switched) loses
        // its classes; the keyboard cursor and the picked-up run are put back.
        function remark() {
            if (curSel) setCursor(curSel.p, curSel.n);
            if (pickSel) setPicked(pickSel.p, pickSel.n);
        }
        function setCursor(p, n) {
            curSel = p == null ? null : { p: p, n: n };
            if (cursorEl) cursorEl.classList.remove('cur');
            cursorEl = null;
            if (p == null || !st) return;
            var pile = st.piles[p];
            cursorEl = pile.length ? cards.get(S.uid(pile[Math.max(0, pile.length - n)])) : slots[p];
            if (cursorEl) cursorEl.classList.add('cur');
        }
        function setPicked(p, n) {
            pickSel = p == null ? null : { p: p, n: n };
            pickedEls.forEach(function (node) { node.classList.remove('picked'); });
            pickedEls = [];
            if (p == null || !st) return;
            var pile = st.piles[p];
            for (var i = pile.length - n; i < pile.length; i++) {
                var node = cards.get(S.uid(pile[i]));
                if (node) { node.classList.add('picked'); pickedEls.push(node); }
            }
        }

        // The foundations' top cards rise one after another: the game is won.
        function wave() {
            if (motion !== 'waapi' || cfg.motion() !== 'full' || !st) return 0;
            var list = [], i;
            for (i = 0; i < def.piles; i++) {
                if (def.kinds[i] !== 'found' || !st.piles[i].length) continue;
                var pile = st.piles[i], c = game === 'spider' ? pile[0] : pile[pile.length - 1];
                var node = cards.get(S.uid(c));
                if (node) list.push(node);
            }
            list.forEach(function (node, j) {
                var a = at(node._fx, node._fy), up = at(node._fx, node._fy - M.ch * 0.22) + ' scale(1.08)';
                anim(node, [{ transform: a }, { transform: up, offset: 0.4 }, { transform: a }], { duration: 520, delay: j * 90, easing: 'cubic-bezier(.3,.7,.4,1)' });
            });
            return list.length ? 520 + (list.length - 1) * 90 : 0;
        }

        // A detached card face for pictures outside the table (the rules).
        function face(c) {
            var node = document.createElement('div');
            paintFace(node, c);
            node.setAttribute('aria-hidden', 'true');
            return node;
        }

        return {
            el: el,
            setGame: setGame,
            render: render,
            relayout: relayout,
            settle: settle,
            busy: function () { return !!seq; },
            // runs fn when nothing is in the air (now, if nothing is)
            idle: function (fn) { if (!seq) { fn(); return; } idleQ.push(fn); },
            options: function (o) {
                if ('lefty' in o) opt.lefty = !!o.lefty;
                if ('xl' in o) opt.xl = !!o.xl;
            },
            metrics: function () { return M; },
            isSide: function () { return opt.side; },
            isLight: function () { return light; },
            motionKind: function () { return motion; },
            showHint: showHint,
            clearHint: clearHint,
            shake: shake,
            tip: tip,
            hideTip: hideTip,
            wave: wave,
            face: face,
            setCursor: setCursor,
            setPicked: setPicked,
            repaintFaces: function () { settle(); cards.forEach(function (node) { node._c = -1; }); if (st) render(st, { instant: true }); },
            nodeOf: function (c) { return cards.get(S.uid(c)); },
            slotOf: function (p) { return slots[p]; },
            dragging: function () { return !!(drag && drag.started); },
            cancelDrag: function () { if (drag) endDrag(false); }
        };
    }

    root.SolTable = { create: create };
})(typeof self !== 'undefined' ? self : this);
